import React, { FC, useState } from 'react';
import {
  Box,
  Icon,
  IconSize,
  Text,
  ToggleSwitch,
  Button,
  ButtonAppearance,
  FormControl,
  TextFieldSize,
} from '@bechtel/gds';
import { TextWithEllipsis } from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';
import { Formik } from 'formik';
import { EditBarStyled, EditItemStyled, ItemStyled, TextInputStyled } from './styled';
import { DataVaultItemProps } from './types';
import { DataVault } from '../types';

const LocationDescription: FC<{ dataVault: DataVault }> = ({ dataVault }) => (
  <Box flexDirection={{ sm: 'column' }} style={{ width: '400px' }}>
    <Box mb={{ sm: '300' }}>
      <Text family="graphik" color="grey.1000" size="300">
        {dataVault.name}
      </Text>
    </Box>
    <TextWithEllipsis family="graphik" color="grey.600" size="300" title={dataVault.locationDescription}>
      {dataVault.locationDescription}
    </TextWithEllipsis>
  </Box>
);

export const DataVaultItem: FC<DataVaultItemProps> = ({ dataVault, setSelectedDataVaultIds }) => {
  const [isEdit, setIsEdit] = useState(false);

  return (
    <>
      {isEdit ? (
        <EditItemStyled data-testid={`data-vault-item-edit-${dataVault.id}`}>
          <Formik
            enableReinitialize={true}
            initialValues={{
              latitude: dataVault.location?.latitude,
              longitude: dataVault.location?.longitude,
              isActive: dataVault?.isActive,
            }}
            onSubmit={(values) => {}}
          >
            {({ setFieldValue, values, dirty, isValid, handleSubmit }) => {
              return (
                <>
                  <Box gap={{ sm: '450' }} style={{ height: '100%' }}>
                    <EditBarStyled />
                    <Box pt={{ sm: '450' }}>
                      <LocationDescription dataVault={dataVault} />
                    </Box>
                  </Box>
                  <Box pb={{ sm: '450' }} pt={{ sm: '550' }} gap={{ sm: '450' }}>
                    <FormControl
                      label={
                        <Text family="graphik" size="300" color="bechtel.blue.800">
                          <Trans>Latitude</Trans>
                        </Text>
                      }
                      css={`
                        flex-grow: 1;
                      `}
                    >
                      <TextInputStyled
                        size={TextFieldSize.LARGE}
                        value={values.latitude}
                        placeholder={t`Enter Latitude`}
                        onChange={(event) => {
                          setFieldValue('latitude', event.target.value);
                        }}
                      />
                    </FormControl>
                    <FormControl
                      label={
                        <Text family="graphik" size="300" color="bechtel.blue.800">
                          <Trans>Longitude</Trans>
                        </Text>
                      }
                      css={`
                        flex-grow: 1;
                      `}
                    >
                      <TextInputStyled
                        size={TextFieldSize.LARGE}
                        value={values.longitude}
                        placeholder={t`Enter Longitude`}
                        onChange={(event) => {
                          setFieldValue('longitude', event.target.value);
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box ml={{ sm: '450' }}>
                    <ToggleSwitch
                      isToggled={values.isActive ? true : false}
                      onToggle={() => setFieldValue('isActive', !values.isActive)}
                    />
                  </Box>
                  <Box gap={{ sm: '700' }}>
                    <Button
                      appearance={ButtonAppearance.TERTIARY}
                      onClick={() => {
                        setIsEdit(false);
                        setSelectedDataVaultIds((prev) => {
                          const next = new Set(prev);
                          next.delete(dataVault.id);
                          return next;
                        });
                      }}
                    >
                      <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                        <Trans>Cancel</Trans>
                      </Text>
                    </Button>
                    <Button
                      appearance={ButtonAppearance.TERTIARY}
                      disabled={!dirty || !isValid}
                      onClick={() => handleSubmit()}
                    >
                      <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                        <Trans>Save</Trans>
                      </Text>
                    </Button>
                  </Box>
                </>
              );
            }}
          </Formik>
        </EditItemStyled>
      ) : (
        <ItemStyled
          data-testid={`data-vault-item-${dataVault.id}`}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
          px={{ sm: '550' }}
          pb={{ sm: '550' }}
          pt={{ sm: '500' }}
          isActive={dataVault.isActive ? true : false}
        >
          <LocationDescription dataVault={dataVault} />
          <Box>
            <Text family="graphik" color="grey.600" size="300">
              {dataVault.location?.latitude}&#176; N, {dataVault.location?.longitude}&#176; W
            </Text>
          </Box>
          <Box alignItems={{ sm: 'center' }}>
            <Icon
              icon="edit"
              size={IconSize.LARGE}
              fillColor="blue.800"
              onClick={() => {
                setIsEdit(true);
                setSelectedDataVaultIds((prev) => {
                  return new Set(prev).add(dataVault.id);
                });
              }}
              style={{ cursor: 'pointer' }}
            />
            <Box ml={{ sm: '800' }}>
              <ToggleSwitch isToggled={dataVault.isActive ? true : false} />
            </Box>
          </Box>
        </ItemStyled>
      )}
    </>
  );
};
